import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import moment from 'moment';
import { Area, AreaChart, CartesianGrid, ResponsiveContainer, Tooltip, XAxis, YAxis } from 'recharts';
import RefreshCheckApi from '../../communication_system/axios_apis/refresh_check/refresh_check_api';
import { getActionLabel, getPlatformLabel, getScreenLabel } from './behaviorLabels';
import WelcomeGiftFunnel from './WelcomeGiftFunnel';
import * as S from './BehaviorAnalyticsPage.style';

const number = new Intl.NumberFormat('ko-KR');
const quickRanges = [7, 14, 30];
const flowColors = ['#f1efff', '#edf5ff', '#eff9f3', '#fff4e8'];

const makeRange = (days) => ({
    startDate: moment()
        .subtract(days - 1, 'days')
        .format('YYYY-MM-DD'),
    endDate: moment().format('YYYY-MM-DD'),
    days,
});

const formatDate = (date) => moment(date).format('M/D');
const formatDuration = (durationMs) => {
    const minutes = durationMs / 60000;
    if (minutes >= 1) return `${number.format(Number(minutes.toFixed(1)))}분`;
    return `${number.format(Math.round(durationMs / 1000))}초`;
};

const BehaviorAnalyticsPage = () => {
    const [activeTab, setActiveTab] = useState('trip');
    const requestId = useRef(0);
    const [range, setRange] = useState(() => makeRange(7));
    const [draftRange, setDraftRange] = useState(() => makeRange(7));
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(false);

    const load = useCallback(async () => {
        const id = ++requestId.current;
        setLoading(true);
        setError(false);
        try {
            const response = await RefreshCheckApi.get('/adminBehaviorAnalytics/overview', {
                params: { startDate: range.startDate, endDate: range.endDate },
            });
            if (id === requestId.current) setData(response.data.data);
        } catch (_) {
            if (id === requestId.current) setError(true);
        } finally {
            if (id === requestId.current) setLoading(false);
        }
    }, [range]);

    useEffect(() => {
        load();
        return () => {
            requestId.current += 1;
        };
    }, [load]);

    const insights = useMemo(() => {
        const topScreen = data?.topScreens?.[0];
        const topAction = data?.topActions?.[0];
        const busiest = data?.daily?.reduce((best, day) => (!best || day.totalEvents > best.totalEvents ? day : best), null);
        return { topScreen, topAction, busiest };
    }, [data]);

    const chooseQuickRange = (days) => {
        const next = makeRange(days);
        setDraftRange(next);
        setRange(next);
    };

    const applyCustomRange = () => {
        if (!draftRange.startDate || !draftRange.endDate || draftRange.startDate > draftRange.endDate) return;
        setRange({ ...draftRange, days: null });
    };

    const header = (
        <>
            <PageHeader range={range} draftRange={draftRange} setDraftRange={setDraftRange} chooseQuickRange={chooseQuickRange} applyCustomRange={applyCustomRange} />
            <S.ProductTabs role="tablist" aria-label="분석 서비스">
                {[
                    { key: 'trip', label: '시놀트립' },
                    { key: 'welcome', label: '웰컴기프트' },
                ].map((tab) => (
                    <S.ProductTab
                        key={tab.key}
                        id={`tab-${tab.key}`}
                        type="button"
                        role="tab"
                        aria-selected={activeTab === tab.key}
                        aria-controls="funnel-panel"
                        $active={activeTab === tab.key}
                        onClick={() => setActiveTab(tab.key)}
                    >
                        {tab.label}
                    </S.ProductTab>
                ))}
            </S.ProductTabs>
        </>
    );

    if (loading) {
        return (
            <S.Page>
                {header}
                <S.StateCard>고객 활동을 읽어오는 중입니다.</S.StateCard>
            </S.Page>
        );
    }

    if (error) {
        return (
            <S.Page>
                {header}
                <S.StateCard>
                    <div>
                        <h2>데이터를 불러오지 못했습니다</h2>
                        <p>잠시 뒤 다시 시도해 주세요. 앱 이용에는 영향이 없습니다.</p>
                        <button type="button" onClick={load}>
                            다시 불러오기
                        </button>
                    </div>
                </S.StateCard>
            </S.Page>
        );
    }

    if (activeTab === 'welcome') {
        return (
            <S.Page>
                {header}
                <div id="funnel-panel" role="tabpanel" aria-labelledby="tab-welcome">
                    <WelcomeGiftFunnel data={data?.welcomeGiftFunnel} collectionReady={data?.collectionReady} />
                </div>
            </S.Page>
        );
    }

    if (!data?.collectionReady || data.summary.totalEvents === 0) {
        return (
            <S.Page>
                {header}
                <S.Notice>개인을 식별할 수 있는 입력 내용은 수집하지 않고, 화면과 버튼 행동만 집계합니다.</S.Notice>
                <S.StateCard>
                    <div>
                        <h2>선택한 기간에는 아직 행동 데이터가 없습니다</h2>
                        <p>새 수집 버전의 앱을 이용한 고객 활동이 생기면 화면 조회와 주요 행동이 이곳에 자동으로 쌓입니다.</p>
                    </div>
                </S.StateCard>
            </S.Page>
        );
    }

    const userDwell = data.dwellDistribution?.users || {};
    const sessionDwell = data.dwellDistribution?.sessions || {};
    const averageUserDwell = userDwell.averageMinutes ?? (data.summary.uniqueUsers ? Number(((data.summary.totalDwellMinutes || 0) / data.summary.uniqueUsers).toFixed(1)) : 0);
    const averageSessionDwell = sessionDwell.averageMinutes ?? data.summary.averageSessionMinutes ?? 0;
    const maxActionCount = data.topActions[0]?.eventCount || 1;
    return (
        <S.Page>
            {header}
            <S.TabPanel id="funnel-panel" role="tabpanel" aria-labelledby="tab-trip">
                <S.Notice>개인을 식별할 수 있는 입력 내용은 수집하지 않고, 화면과 버튼 행동만 집계합니다. 영역별 사용자는 서로 중복될 수 있습니다.</S.Notice>

                <S.KpiGrid>
                    <Kpi label="활동한 사용자" value={`${number.format(data.summary.uniqueUsers)}명`} hint="선택 기간 중 한 번 이상 활동" tint="#e9f3ff" />
                    <Kpi label="기록된 행동" value={`${number.format(data.summary.totalEvents)}회`} hint="화면 보기·누르기·탐색 합계" tint="#efedff" />
                    <Kpi label="방문 묶음" value={`${number.format(data.summary.sessions)}회`} hint="앱을 열고 이어서 활동한 단위" tint="#eaf8ef" />
                    <Kpi label="사용자당 평균 행동" value={`${number.format(data.summary.actionsPerUser)}회`} hint="한 사람이 남긴 평균 활동량" tint="#fff1e3" />
                    <Kpi label="사용자당 평균 체류" value={`${number.format(averageUserDwell)}분`} hint="선택 기간 한 사람의 누적 평균" tint="#e8f7f7" />
                    <Kpi label="방문당 평균 체류" value={`${number.format(averageSessionDwell)}분`} hint="체류가 기록된 방문 한 건의 평균" tint="#f5ecff" />
                </S.KpiGrid>

                <S.InsightStrip>
                    <S.Insight>
                        가장 많이 본 화면
                        <strong>{insights.topScreen ? `${getScreenLabel(insights.topScreen.name)} · ${number.format(insights.topScreen.userCount)}명` : '아직 없음'}</strong>
                    </S.Insight>
                    <S.Insight>
                        가장 많이 한 행동
                        <strong>{insights.topAction ? `${getActionLabel(insights.topAction.name)} · ${number.format(insights.topAction.eventCount)}회` : '아직 없음'}</strong>
                    </S.Insight>
                    <S.Insight>
                        활동이 가장 많았던 날<strong>{insights.busiest ? `${moment(insights.busiest.date).format('M월 D일')} · ${number.format(insights.busiest.totalEvents)}회` : '아직 없음'}</strong>
                    </S.Insight>
                </S.InsightStrip>

                <DwellDistribution data={data.dwellDistribution} />

                <MembershipFunnel data={data.membershipFunnel} detail={data.membershipDetailInsights} />

                <S.TwoColumn>
                    <S.Card>
                        <S.CardHeader>
                            <div>
                                <S.CardTitle>날짜별 이용 흐름</S.CardTitle>
                                <S.CardHelp>행동량이 갑자기 늘거나 줄어든 날을 찾아보세요.</S.CardHelp>
                            </div>
                        </S.CardHeader>
                        <S.ChartBox>
                            <ResponsiveContainer width="100%" height="100%">
                                <AreaChart data={data.daily} margin={{ top: 8, right: 10, left: -16, bottom: 0 }}>
                                    <defs>
                                        <linearGradient id="behaviorArea" x1="0" y1="0" x2="0" y2="1">
                                            <stop offset="5%" stopColor="#6255e7" stopOpacity={0.26} />
                                            <stop offset="95%" stopColor="#6255e7" stopOpacity={0} />
                                        </linearGradient>
                                    </defs>
                                    <CartesianGrid stroke="#eceef4" strokeDasharray="3 3" vertical={false} />
                                    <XAxis dataKey="date" tickFormatter={formatDate} tick={{ fill: '#7b8195', fontSize: 11 }} axisLine={false} tickLine={false} />
                                    <YAxis tick={{ fill: '#7b8195', fontSize: 11 }} axisLine={false} tickLine={false} allowDecimals={false} />
                                    <Tooltip
                                        labelFormatter={(value) => moment(value).format('M월 D일')}
                                        formatter={(value, name) => [`${number.format(value)}${name === 'uniqueUsers' ? '명' : '회'}`, name === 'uniqueUsers' ? '활동 사용자' : '행동']}
                                    />
                                    <Area type="monotone" dataKey="totalEvents" stroke="#6255e7" strokeWidth={3} fill="url(#behaviorArea)" />
                                    <Area type="monotone" dataKey="uniqueUsers" stroke="#35a978" strokeWidth={2} fill="transparent" />
                                </AreaChart>
                            </ResponsiveContainer>
                        </S.ChartBox>
                    </S.Card>

                    <S.Card>
                        <S.CardHeader>
                            <div>
                                <S.CardTitle>많이 본 화면</S.CardTitle>
                                <S.CardHelp>실제로 도달한 사용자 수 기준입니다.</S.CardHelp>
                            </div>
                        </S.CardHeader>
                        <RankList rows={data.topScreens.slice(0, 7)} labeler={getScreenLabel} max={data.topScreens[0]?.userCount || 1} countKey="userCount" unit="명" />
                    </S.Card>
                </S.TwoColumn>

                <S.TwoColumn>
                    <S.Card>
                        <S.CardHeader>
                            <div>
                                <S.CardTitle>오래 머문 화면</S.CardTitle>
                                <S.CardHelp>화면별 전체 체류시간과 한 번 방문했을 때의 평균입니다.</S.CardHelp>
                            </div>
                        </S.CardHeader>
                        <DwellRankList rows={data.topScreenDwell || []} />
                    </S.Card>

                    <S.Card>
                        <S.CardHeader>
                            <div>
                                <S.CardTitle>사용자가 많이 한 행동</S.CardTitle>
                                <S.CardHelp>화면 안에서 어떤 메뉴와 콘텐츠에 반응했는지 보여줍니다.</S.CardHelp>
                            </div>
                        </S.CardHeader>
                        <RankList rows={data.topActions.slice(0, 10)} labeler={getActionLabel} max={maxActionCount} countKey="eventCount" unit="회" showUsers />
                    </S.Card>
                </S.TwoColumn>

                <S.TwoColumn>
                    <S.Card style={{ gridColumn: '1 / -1' }}>
                        <S.CardHeader>
                            <div>
                                <S.CardTitle>관심 영역 도달 사용자</S.CardTitle>
                                <S.CardHelp>영역 간 순서가 아닌, 각 관심 영역에 닿은 사용자 규모입니다.</S.CardHelp>
                            </div>
                        </S.CardHeader>
                        <S.FlowGrid>
                            {data.interestFlow.map((item, index) => (
                                <S.FlowItem key={item.key} $background={flowColors[index]}>
                                    <S.FlowName>{item.name}</S.FlowName>
                                    <S.FlowValue>{number.format(item.userCount)}명</S.FlowValue>
                                </S.FlowItem>
                            ))}
                        </S.FlowGrid>
                        <S.CardHeader style={{ marginTop: 24, marginBottom: 6 }}>
                            <div>
                                <S.CardTitle>이용 환경</S.CardTitle>
                                <S.CardHelp>테스트 결과를 볼 때 버전 편중 여부를 함께 확인하세요.</S.CardHelp>
                            </div>
                        </S.CardHeader>
                        <S.SegmentRow>
                            {data.platforms.map((item) => (
                                <S.SegmentChip key={item.name}>
                                    {getPlatformLabel(item.name)}
                                    <strong>{number.format(item.userCount)}명</strong>
                                </S.SegmentChip>
                            ))}
                        </S.SegmentRow>
                        <S.SegmentRow>
                            {data.appVersions.map((item) => (
                                <S.SegmentChip key={item.name}>
                                    v{item.name}
                                    <strong>{number.format(item.userCount)}명</strong>
                                </S.SegmentChip>
                            ))}
                        </S.SegmentRow>
                    </S.Card>
                </S.TwoColumn>
            </S.TabPanel>
        </S.Page>
    );
};

const PageHeader = ({ range, draftRange, setDraftRange, chooseQuickRange, applyCustomRange }) => (
    <S.Header>
        <div>
            <S.Eyebrow>FUNNEL ANALYSIS</S.Eyebrow>
            <S.Title>퍼널분석</S.Title>
            <S.Description>시놀트립과 웰컴기프트의 단계별 도달과 이탈을 살펴봅니다. 조회 기간은 한국 시간 기준입니다.</S.Description>
        </div>
        <S.PeriodPanel aria-label="조회 기간">
            {quickRanges.map((days) => (
                <S.QuickButton key={days} type="button" $active={range.days === days} onClick={() => chooseQuickRange(days)}>
                    {days}일
                </S.QuickButton>
            ))}
            <S.DateInput aria-label="시작일" type="date" value={draftRange.startDate} onChange={(event) => setDraftRange((prev) => ({ ...prev, startDate: event.target.value }))} />
            <span>–</span>
            <S.DateInput aria-label="종료일" type="date" value={draftRange.endDate} onChange={(event) => setDraftRange((prev) => ({ ...prev, endDate: event.target.value }))} />
            <S.ApplyButton type="button" onClick={applyCustomRange}>
                적용
            </S.ApplyButton>
        </S.PeriodPanel>
    </S.Header>
);

const Kpi = ({ label, value, hint, tint }) => (
    <S.KpiCard $tint={tint}>
        <S.KpiLabel>{label}</S.KpiLabel>
        <S.KpiValue>{value}</S.KpiValue>
        <S.KpiHint>{hint}</S.KpiHint>
    </S.KpiCard>
);

const DwellDistribution = ({ data }) => {
    const users = data?.users;
    const sessions = data?.sessions;
    if (!users?.sampleSize) return null;

    const maxCount = Math.max(...users.buckets.map((bucket) => bucket.count), 1);
    return (
        <S.DwellSection>
            <S.SectionHeading>
                <div>
                    <S.CardTitle>사용자 체류시간 분포</S.CardTitle>
                    <S.CardHelp>선택 기간 동안 같은 사용자의 여러 방문을 합산했습니다. 평균과 중앙값의 차이로 소수 장기 사용자의 영향을 확인할 수 있습니다.</S.CardHelp>
                </div>
                <S.DwellSample>
                    체류 측정 사용자 <strong>{number.format(users.sampleSize)}명</strong>
                </S.DwellSample>
            </S.SectionHeading>

            <S.DwellSummaryGrid>
                <S.DwellSummary>
                    <span>가장 많은 사용자</span>
                    <strong>{users.modeBucket.label}</strong>
                    <small>
                        {number.format(users.modeBucket.count)}명 · {number.format(users.modeBucket.percentage)}%
                    </small>
                </S.DwellSummary>
                <S.DwellSummary>
                    <span>사용자당 평균</span>
                    <strong>{number.format(users.averageMinutes)}분</strong>
                    <small>선택 기간 누적 체류 기준</small>
                </S.DwellSummary>
                <S.DwellSummary>
                    <span>사용자 중앙값</span>
                    <strong>{number.format(users.medianMinutes)}분</strong>
                    <small>절반은 이 시간 이하로 사용</small>
                </S.DwellSummary>
                <S.DwellSummary>
                    <span>상위 10% 체류 비중</span>
                    <strong>{number.format(users.top10Share)}%</strong>
                    <small>소수 장기 사용자의 평균 영향</small>
                </S.DwellSummary>
            </S.DwellSummaryGrid>

            <S.DwellDistributionList aria-label="사용자 체류시간 구간별 분포">
                {users.buckets.map((bucket) => (
                    <S.DwellBucketRow key={bucket.key}>
                        <S.DwellBucketLabel>{bucket.label}</S.DwellBucketLabel>
                        <S.DwellBar $value={(bucket.count / maxCount) * 100}>
                            <span />
                        </S.DwellBar>
                        <S.DwellBucketValue>
                            <strong>{number.format(bucket.count)}명</strong>
                            <span>{number.format(bucket.percentage)}%</span>
                        </S.DwellBucketValue>
                    </S.DwellBucketRow>
                ))}
            </S.DwellDistributionList>

            {sessions?.sampleSize > 0 && (
                <S.VisitDwellSummary>
                    방문 1회 기준 · 평균 <strong>{number.format(sessions.averageMinutes)}분</strong> · 중앙값 <strong>{number.format(sessions.medianMinutes)}분</strong> · 가장 많은 구간{' '}
                    <strong>{sessions.modeBucket.label}</strong> ({number.format(sessions.modeBucket.percentage)}%)
                </S.VisitDwellSummary>
            )}
        </S.DwellSection>
    );
};

const MembershipFunnel = ({ data, detail }) => {
    const steps = data?.steps || [];
    const started = data?.startedSessions || 0;
    return (
        <S.FunnelSection>
            <S.SectionHeading>
                <div>
                    <S.CardTitle>시놀트립 결제 퍼널</S.CardTitle>
                    <S.CardHelp>배너 클릭부터 최종 결제 단계까지 같은 방문 안에서 순서대로 이동한 비율입니다.</S.CardHelp>
                </div>
                <S.FunnelTotal>
                    전체 전환율 <strong>{number.format(data?.overallConversionRate || 0)}%</strong>
                </S.FunnelTotal>
            </S.SectionHeading>
            {started === 0 ? (
                <S.FunnelEmpty>선택한 기간에는 배너에서 시작한 시놀트립 결제 경로가 아직 없습니다.</S.FunnelEmpty>
            ) : (
                <S.FunnelSteps>
                    {steps.map((step, index) => (
                        <S.FunnelStep key={step.key}>
                            <S.FunnelStepIndex>{index + 1}</S.FunnelStepIndex>
                            <S.FunnelStepName>{step.name}</S.FunnelStepName>
                            <S.FunnelStepValue>{number.format(step.sessionCount)}회</S.FunnelStepValue>
                            <S.FunnelStepMeta>{index === 0 ? '퍼널 시작' : `이전 단계 대비 ${number.format(step.conversionFromPrevious)}%`}</S.FunnelStepMeta>
                            {index > 0 && <S.DropOff $warning={step.dropOffRate >= 50}>이탈 {number.format(step.dropOffRate)}%</S.DropOff>}
                        </S.FunnelStep>
                    ))}
                </S.FunnelSteps>
            )}
            <S.DetailInsightGrid>
                <S.DetailInsight>
                    <span>상세 평균 체류</span>
                    <strong>{formatDuration((detail?.averageDwellSeconds || 0) * 1000)}</strong>
                    <small>{number.format(detail?.sessionCount || 0)}회 방문 기준</small>
                </S.DetailInsight>
                <S.DetailInsight>
                    <span>평균 최대 스크롤</span>
                    <strong>{number.format(detail?.averageScrollPercent || 0)}%</strong>
                    <small>상세 내용을 어디까지 봤는지</small>
                </S.DetailInsight>
                <S.DetailInsight>
                    <span>75% 이상 읽은 비율</span>
                    <strong>{number.format(detail?.deepScrollRate || 0)}%</strong>
                    <small>상세페이지 깊은 탐색</small>
                </S.DetailInsight>
                <S.DetailInsight>
                    <span>신청 버튼 노출 대비 클릭</span>
                    <strong>{number.format(detail?.ctaClickRate || 0)}%</strong>
                    <small>
                        {number.format(detail?.ctaImpressions || 0)}회 노출 · {number.format(detail?.ctaClicks || 0)}회 클릭
                    </small>
                </S.DetailInsight>
            </S.DetailInsightGrid>
        </S.FunnelSection>
    );
};

const RankList = ({ rows, labeler, max, countKey, unit, showUsers = false }) => (
    <S.RankList>
        {rows.map((item, index) => (
            <S.RankItem key={item.name}>
                <S.RankNumber>{index + 1}</S.RankNumber>
                <div>
                    <S.RankName>{labeler(item.name)}</S.RankName>
                    {showUsers && <S.RankMeta>{number.format(item.userCount)}명이 사용</S.RankMeta>}
                </div>
                <S.RankCount>
                    {number.format(item[countKey])}
                    {unit}
                </S.RankCount>
                <S.Progress $value={(item[countKey] / max) * 100}>
                    <span />
                </S.Progress>
            </S.RankItem>
        ))}
    </S.RankList>
);

const DwellRankList = ({ rows }) => {
    const max = rows[0]?.totalDurationMs || 1;
    return (
        <S.RankList>
            {rows.slice(0, 10).map((item, index) => (
                <S.RankItem key={item.name}>
                    <S.RankNumber>{index + 1}</S.RankNumber>
                    <div>
                        <S.RankName>{getScreenLabel(item.name)}</S.RankName>
                        <S.RankMeta>
                            평균 {formatDuration(item.averageDurationMs)} · {number.format(item.userCount)}명
                        </S.RankMeta>
                    </div>
                    <S.RankCount>{formatDuration(item.totalDurationMs)}</S.RankCount>
                    <S.Progress $value={(item.totalDurationMs / max) * 100}>
                        <span />
                    </S.Progress>
                </S.RankItem>
            ))}
        </S.RankList>
    );
};

export default BehaviorAnalyticsPage;
